import { useId } from "react";
import { PageTitle } from "lola-framework-ui-test";
import { FlowHeroMark } from "./FlowHeroMark";
import { useUnsupportedLandscape } from "../hooks/useUnsupportedLandscape";
import { TITLE_COLOR } from "../constants";
import { useStore } from "../store/globalState";
import "./RotateToPortraitOverlay.css";

interface RotateToPortraitOverlayProps {
  title: string;
  subtitle: string;
  className?: string;
}

const RotateDeviceIcon = ({ colors }: { colors: [string, string] }) => {
  const gradientId = `rotate-device-stroke-${useId()}`;
  return (
    <svg
      className="rotate-to-portrait-overlay__icon"
      width="65"
      height="65"
      viewBox="0 0 80 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient
          id={gradientId}
          x1="0"
          y1="0"
          x2="80"
          y2="80"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colors[0]} />
          <stop offset="1" stopColor={colors[1]} />
        </linearGradient>
      </defs>
      <g
        stroke={`url(#${gradientId})`}
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <g transform="rotate(45 40 40)">
          <rect x="27" y="16" width="26" height="48" rx="6.5" />
          <path d="M35 57h10" />
        </g>
        <path d="M44.2 10.3A30 30 0 0 0 10.3 35.8" />
        <path d="M15.3 30.6 10.3 35.8 6.9 29.4" />
        <path d="M35.8 69.7A30 30 0 0 0 69.7 44.2" />
        <path d="M64.7 49.4 69.7 44.2 73.1 50.6" />
      </g>
    </svg>
  );
};

/**
 * Full-screen guard shown on touch devices in landscape. Asks the user to
 * rotate back to portrait. Does not unmount routes underneath.
 *
 * Disc + title ride the shared `flow-hero` anchor (`--hero-disc-center-y`), so
 * the mark lands on the same center as welcome / verifying screens.
 */
export const RotateToPortraitOverlay = ({
  title,
  subtitle,
  className,
}: RotateToPortraitOverlayProps) => {
  const shouldBlock = useUnsupportedLandscape();
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const { background, dropzoneColors, iconColors, bodyCopy, backgroundIcon } =
    colorConfig;
  const iconPaint = (dropzoneColors ?? iconColors ?? [
    bodyCopy,
    bodyCopy,
  ]) as [string, string];

  if (!shouldBlock) return null;

  const rootClass = ["rotate-to-portrait-overlay", className]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={rootClass}
      role="alert"
      aria-live="polite"
      style={{ background }}
    >
      <div className="rotate-to-portrait-overlay__content flow-hero-section">
        <div className="flow-hero">
          <FlowHeroMark backgroundIcon={backgroundIcon}>
            <RotateDeviceIcon colors={iconPaint} />
          </FlowHeroMark>
          <div className="rotate-to-portrait-overlay__copy">
            <PageTitle
              highlight={title}
              highlightColor={TITLE_COLOR}
              secudnary={subtitle}
              secudnaryColor={TITLE_COLOR}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
